import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const ResearchDevelopment: React.FC = () => {
  const researchAreas = [
    {
      title: "Sustainable Technology Innovation",
      description: "Developing cutting-edge sustainable technologies that address environmental challenges while driving economic growth.",
      icon: "🌱",
      features: ["Green energy solutions", "Eco-friendly materials", "Carbon reduction technologies"]
    },
    {
      title: "Agricultural Engineering Research",
      description: "Pioneering agricultural technologies to improve food security and farming efficiency in Ekumfiman.",
      icon: "🚜",
      features: ["Smart farming systems", "Crop optimization", "Irrigation technology"]
    },
    {
      title: "Industrial Automation",
      description: "Researching and developing automation solutions to enhance manufacturing efficiency and productivity.",
      icon: "⚙️",
      features: ["Process automation", "Quality control systems", "Predictive maintenance"]
    },
    {
      title: "Renewable Energy Systems",
      description: "Advancing renewable energy technologies including solar, wind, and bioenergy solutions.",
      icon: "⚡",
      features: ["Solar power systems", "Wind energy", "Biomass conversion"]
    }
  ];

  const capabilities = [
    "Advanced prototyping and testing",
    "Collaborative research partnerships",
    "Technology transfer programs",
    "Innovation incubation",
    "Patent development and protection",
    "Market analysis and feasibility studies"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-spin-slow">
                <EngineeringIcon type="gear" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">Research & Development</h1>
            <p className="text-xl mb-8">
              Pioneering breakthrough engineering solutions through cutting-edge research and development. 
              We innovate across all engineering disciplines to create sustainable, efficient, and groundbreaking technologies.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Innovation Hub</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Technology Leadership</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Sustainable Solutions</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Research Areas */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Research Focus Areas</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our R&D efforts are focused on creating innovative solutions that address real-world challenges 
              while driving sustainable development in Ekumfiman.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {researchAreas.map((area, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-pulley-bounce">{area.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{area.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{area.description}</p>
                <ul className="space-y-2">
                  {area.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-orange-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Capabilities */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our R&D Capabilities</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive research and development services to bring innovative ideas to life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((capability, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-orange-50 transition-colors">
                <div className="flex items-center">
                  <EngineeringIcon type="cog" size="md" className="text-orange-500 mr-4" />
                  <span className="font-semibold text-gray-800">{capability}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Call to Action */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Innovate Together?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join us in pushing the boundaries of engineering innovation. Let's create the technologies 
            that will shape the future of Ekumfiman.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-orange-500 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="wrench" size="sm" animated={false} />
              Start a Research Project
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-orange-500 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" />
              Learn More
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchDevelopment; 